/**
 * CR-7750: the customer's review of a supplier's submission, shown inside the
 * Approval Register cell dialog.
 *
 * Submitted values sit beside the current ones so the customer can see what
 * accepting would change before they do it. Until they accept, the register
 * keeps showing the current values and the RAG status is untouched — that is
 * the client's "Pending review please", rendered.
 */
import { useState } from 'react';
import { Check, Loader2, X, Clock } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { qualityAPI } from '../../lib/api';
import type { ApprovalRegisterCell, ApprovalRegisterPending } from '../../lib/api';

export interface PendingReviewPanelProps {
  pending: ApprovalRegisterPending;
  /** The live values, when the supplier is renewing rather than claiming new. */
  current?: ApprovalRegisterCell | null;
  formatDate: (value: string | null | undefined) => string;
  onReviewed: () => void;
}

interface RowProps {
  label: string;
  current: string;
  proposed: string;
}

function CompareRow({ label, current, proposed }: RowProps) {
  const changed = current !== proposed;
  return (
    <div className="grid grid-cols-3 gap-2 py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={changed ? 'line-through text-muted-foreground' : ''}>
        {current || '—'}
      </span>
      <span className={changed ? 'font-medium text-foreground' : ''}>
        {proposed || '—'}
      </span>
    </div>
  );
}

export default function PendingReviewPanel({
  pending,
  current,
  formatDate,
  onReviewed,
}: PendingReviewPanelProps) {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const expiryOf = (
    expiry: string | null, noExpiry: boolean,
  ) => (noExpiry ? 'No expiry applies' : formatDate(expiry));

  const run = async (fn: () => Promise<unknown>, success: string) => {
    try {
      setBusy(true);
      await fn();
      toast.success(success);
      onReviewed();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not complete the review';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50/60 p-3 dark:border-amber-700 dark:bg-amber-900/20">
      <div className="flex items-center justify-between">
        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
          <Clock className="mr-1 h-3 w-3" />
          Awaiting your review
        </Badge>
        <span className="text-xs text-muted-foreground">
          {pending.submitted_by_name ? `${pending.submitted_by_name} · ` : ''}
          {formatDate(pending.submitted_at)}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 border-b pb-1 text-xs font-medium uppercase text-muted-foreground">
        <span>Field</span>
        <span>Current</span>
        <span>Submitted</span>
      </div>
      <CompareRow
        label="Issued"
        current={formatDate(current?.issued_date ?? null)}
        proposed={formatDate(pending.issued_date)}
      />
      <CompareRow
        label="Expiry"
        current={current ? expiryOf(current.expiry_date, current.no_expiry_applies) : ''}
        proposed={expiryOf(pending.expiry_date, pending.no_expiry_applies)}
      />
      <CompareRow
        label="Certificate No."
        current={current?.certificate_number ?? ''}
        proposed={pending.certificate_number}
      />
      <CompareRow
        label="Certificate"
        current={current?.has_certificate ? 'Attached' : 'Not attached'}
        proposed={pending.has_certificate ? 'Attached' : 'Not attached'}
      />
      {pending.notes && (
        <p className="text-sm">
          <span className="text-muted-foreground">Supplier notes: </span>
          {pending.notes}
        </p>
      )}

      {rejecting ? (
        <div className="space-y-2">
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Tell the supplier what to correct"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={busy || !reason.trim()}
              onClick={() => run(
                () => qualityAPI.rejectAccreditationSubmission(pending.id, reason.trim()),
                'Rejected — the supplier has been told why',
              )}
            >
              {busy && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
              Confirm reject
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => setRejecting(true)}
          >
            <X className="mr-1 h-4 w-4" />
            Reject
          </Button>
          <Button
            size="sm"
            disabled={busy}
            onClick={() => run(
              () => qualityAPI.acceptAccreditationSubmission(pending.id),
              'Accepted — the register has been updated',
            )}
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <Check className="mr-1 h-4 w-4" />}
            Accept
          </Button>
        </div>
      )}
    </div>
  );
}
